import {
  faCalendar,
  faComments,
  faHome,
  faUsers,
  faList,
  faStar,
  faFile,
} from '@fortawesome/free-solid-svg-icons'
import React, { useState } from 'react'
import { Brand } from 'ui'
import { Navigator, Layouts } from 'ui'
import { useDebounce } from 'utils'
import { useCurrentUser } from '../../queries/useCurrentUser'
import { useDrawer } from '../../hooks/useDrawer'
import { SearchModalResults } from './SearchModal'
import { ProfileNav } from './ProfileNav'
import { Outlet } from 'react-router-dom'

import { concatStyles, remCalc } from 'utils'
const BrandedLogo = () => <Brand.EpcLogo />

type DrawerDisplayMode = 'compact' | 'expanded' | 'expandedWithSecondary'
const DRAWER_WIDTH = 256
const COMPACT_DRAWER_WIDTH = 60
const DRAWER_LAYOUT_STYLES = 'flex m-0 box-border pt-16'

// Calculate padding based on drawer state
const calculatedPadding = (
  drawerActive: boolean,
  drawerCompact: boolean,
  secondaryDrawerPresent: boolean
) => {
  if (drawerActive) {
    return secondaryDrawerPresent
      ? drawerCompact
        ? remCalc(DRAWER_WIDTH + COMPACT_DRAWER_WIDTH)
        : remCalc(DRAWER_WIDTH * 2)
      : remCalc(DRAWER_WIDTH)
  }

  if (secondaryDrawerPresent) {
    return remCalc(DRAWER_WIDTH)
  }

  return 0
}

export const AppNavigator: React.FC = () => {
  try {
    // Get drawer state and toggle from Redux hook
    const { drawerActive, drawerCompact, toggleDrawer } = useDrawer()
    console.log('AppNavigator: Drawer state from Redux', {
      drawerActive,
      drawerCompact,
    })

    const atPath = (path: string) => {
      try {
        return window ? window.location.pathname.indexOf(path) === 0 : false
      } catch (error) {
        console.error('AppNavigator: Error in atPath check:', error)
        return false
      }
    }

    console.log('AppNavigator: Fetching current user profile')
    const { data: profile } = useCurrentUser()
    console.log('AppNavigator: Profile data loaded:', profile ? 'yes' : 'no')

    const profileName = profile
      ? `${profile.firstName} ${profile.lastName}`
      : null
    const profileImage = profile ? profile.avatarUrl : null
    const profileColor = profile ? profile.color : null

    const [queryString, setQueryString] = useState('')
    const searchQuery = useDebounce(queryString, 450)

    const handleSearchUpdate = (query: string) => {
      console.log('AppNavigator: Search query updated')
      setQueryString(query)
    }

    const renderSearchResults = (onResultClick?: () => void) => {
      console.log('AppNavigator: Rendering search results')
      try {
        return (
          <SearchModalResults
            queryString={searchQuery}
            onResultClick={onResultClick}
          />
        )
      } catch (error) {
        console.error('AppNavigator: Error rendering search results:', error)
        return <div>Error loading search results</div>
      }
    }

    const renderDrawerItems = () => {
      console.log('AppNavigator: Rendering drawer items')
      try {
        return (
          <>
            <Navigator.Item
              to="/app/dashboard"
              active={atPath('/app/dashboard')}
              label="Dashboard"
              icon={faHome}
            />
            <Navigator.Item
              to="/app/matches"
              active={atPath('/app/matches')}
              label="Profile Matches"
              icon={faUsers}
            />
            <Navigator.Item
              to="/app/favorites"
              active={atPath('/app/favorites')}
              label="Favorites"
              icon={faStar}
            />
            <Navigator.Item
              to="/app/events"
              active={atPath('/app/events')}
              label="Events"
              icon={faCalendar}
            />
            <Navigator.Item
              to="/app/directory"
              active={atPath('/app/directory')}
              label="Directory"
              icon={faList}
            />
            <Navigator.Item
              to="/app/messages"
              active={atPath('/app/messages')}
              label="Messages"
              icon={faComments}
            />
            <Navigator.Item
              to="/app/resources"
              active={atPath('/app/resources')}
              label="Resources"
              icon={faFile}
            />
          </>
        )
      } catch (error) {
        console.error('AppNavigator: Error rendering drawer items:', error)
        return <></>
      }
    }

    return (
      <Layouts.AppNavigator
        // Pass drawer state and toggle function as props
        drawerActive={drawerActive}
        drawerCompact={drawerCompact}
        toggleDrawer={toggleDrawer}
        renderDrawerItems={renderDrawerItems}
        profileNavigation={ProfileNav}
        profileName={(profileName as string) || 'User'}
        profileColor={(profileColor as string) || '#333333'}
        profileImage={(profileImage as string) || ''}
        onSearchUpdate={handleSearchUpdate}
        renderSearchResults={renderSearchResults}
        logo={BrandedLogo}
        searchEnabled
        menuEnabled
        profileNavEnabled
        hasDrawer
      >
        <Outlet />
      </Layouts.AppNavigator>
    )
  } catch (error) {
    console.error('AppNavigator: Unexpected error:', error)
    return (
      <div style={{ padding: 20 }}>
        <h2>Error loading Application</h2>
        <p>There was an error loading the application interface.</p>
        <details>
          <summary>Error details</summary>
          <pre>{String(error)}</pre>
        </details>
      </div>
    )
  }
}
